import { useEffect, useState } from "react";
import { UserSidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet as WalletIcon, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle2, XCircle, Send, Pencil, Trash2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { formatMoney } from "@/lib/utils";

const StatusPill = ({ s }) => {
  const map = {
    PENDING: { cls: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30", icon: Clock, label: "Na čekanju" },
    APPROVED: { cls: "bg-green-500/20 text-green-400 border-green-500/30", icon: CheckCircle2, label: "Odobreno" },
    REJECTED: { cls: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle, label: "Odbijeno" },
    COMPLETED: { cls: "bg-green-500/20 text-green-400 border-green-500/30", icon: CheckCircle2, label: "Uspješno" },
  };
  const it = map[s] || { cls: "bg-white/10 text-white/70 border-white/20", icon: Clock, label: s };
  const I = it.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${it.cls}`}>
      <I size={12} /> {it.label}
    </span>
  );
};

export default function Wallet() {
  const [w, setW] = useState({ balance: 0, currency: "EUR", transactions: [], payout_requests: [] });
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const delTx = async () => {
    try { await api.delete(`/wallet/transactions/${confirmDel.id}`); toast.success("Transakcija uklonjena"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const load = async () => {
    try { const { data } = await api.get("/wallet/me"); setW(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const request = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Neispravan iznos"); return; }
    if (amt > w.balance) { toast.error("Iznos veći od dostupnog salda"); return; }
    setBusy(true);
    try {
      await api.post("/wallet/payout-request", { amount: amt, note });
      toast.success("Zahtjev za isplatu poslan adminu");
      setOpen(false); setAmount(""); setNote(""); load();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  const pending = w.payout_requests.find((p) => p.status === "PENDING");

  return (
    <div className="stream-dark flex flex-col lg:flex-row min-h-screen">
      <UserSidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-10 stream-scroll">
        <div className="max-w-4xl">
          <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl text-white mb-6 sm:mb-8">Novčanik</h1>

          {/* Balance Card */}
          <div className="balance-card relative overflow-hidden rounded-3xl mb-8" style={{
            background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 40%, #FFD60A22 100%)",
            border: "1px solid rgba(255,214,10,0.25)"
          }}>
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FFD60A] blur-[100px] opacity-20"></div>
            <div className="relative p-6 sm:p-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FFD60A] flex items-center justify-center">
                  <WalletIcon size={22} className="text-black" />
                </div>
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-widest">Dostupno stanje</div>
                  <div className="text-white/40 text-xs">Ukupno primljeno preko payment linkova</div>
                </div>
              </div>
              <div className="flex items-baseline gap-3">
                <div className="font-heading font-black text-5xl sm:text-6xl text-[#FFD60A]" data-testid="wallet-balance">
                  {formatMoney(w.balance)}
                </div>
                <div className="text-white/60 text-2xl">{w.currency}</div>
              </div>
              <div className="mt-6 flex gap-3 flex-wrap">
                <Button
                  className="btn-yellow rounded-full h-11 px-6"
                  disabled={w.balance <= 0 || !!pending}
                  onClick={() => setOpen(true)}
                  data-testid="request-payout"
                >
                  <Send size={16} className="mr-2" /> Zatraži isplatu
                </Button>
                {pending && <div className="text-yellow-300 text-sm self-center">Vaš zahtjev je na čekanju</div>}
              </div>
            </div>
          </div>

          {/* Payout Requests */}
          {w.payout_requests.length > 0 && (
            <div className="mb-8">
              <h2 className="font-heading font-bold text-xl text-white mb-3">Moji zahtjevi za isplatu</h2>
              <div className="card-dark divide-y divide-white/5">
                {w.payout_requests.map((p) => (
                  <div key={p.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`payout-req-${p.id}`}>
                    <div>
                      <div className="text-white font-semibold">{formatMoney(p.amount)} {p.currency}</div>
                      <div className="text-white/50 text-xs">{new Date(p.created_at).toLocaleString("hr-HR")}</div>
                      {p.note && <div className="text-white/60 text-sm mt-1">"{p.note}"</div>}
                    </div>
                    <StatusPill s={p.status} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transactions */}
          <div>
            <h2 className="font-heading font-bold text-xl text-white mb-3">Transakcije</h2>
            <div className="card-dark divide-y divide-white/5">
              {w.transactions.length === 0 ? (
                <div className="p-8 text-center text-white/50">Još nema transakcija na novčaniku.</div>
              ) : w.transactions.map((t) => (
                <div key={t.id} className="p-4 flex items-center gap-3" data-testid={`tx-${t.id}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${t.type === "CREDIT" ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>
                    {t.type === "CREDIT" ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">{t.description}</div>
                    <div className="text-white/40 text-xs">{new Date(t.created_at).toLocaleString("hr-HR")}</div>
                  </div>
                  <div className={`font-heading font-bold whitespace-nowrap ${t.type === "CREDIT" ? "text-green-400" : "text-red-400"}`}>
                    {t.type === "CREDIT" ? "+" : "-"}{formatMoney(t.amount)} {t.currency}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-8 text-white/60 hover:bg-white/10" onClick={() => setDetail(t)} data-testid={`tx-edit-${t.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:bg-red-500/10" onClick={() => setConfirmDel(t)} data-testid={`tx-del-${t.id}`}><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-[#171717] border-white/10 text-white">
            <DialogHeader><DialogTitle>Zahtjev za isplatu na Stripe</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="text-white/60 text-sm">Dostupno: <span className="text-[#FFD60A] font-semibold">{formatMoney(w.balance)} {w.currency}</span></div>
              <div>
                <Label className="text-white/80">Iznos</Label>
                <Input type="number" step="0.01" min="1" max={w.balance} value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="payout-amount" />
              </div>
              <div>
                <Label className="text-white/80">Napomena (opcionalno)</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="payout-note" />
              </div>
              <p className="text-white/40 text-xs">Admin će pregledati vaš zahtjev i ručno prebaciti sredstva na vaš Stripe račun.</p>
            </div>
            <DialogFooter>
              <Button className="btn-yellow rounded-full" onClick={request} disabled={busy} data-testid="payout-submit">
                {busy ? "Šaljemo…" : "Pošalji zahtjev"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="bg-[#171717] border-white/10 text-white" data-testid="tx-detail-dialog">
            <DialogHeader><DialogTitle>Detalji transakcije</DialogTitle></DialogHeader>
            {detail && (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-4"><span className="text-white/50">Vrsta</span><span className={detail.type === "CREDIT" ? "text-green-400 font-semibold" : "text-red-400 font-semibold"}>{detail.type === "CREDIT" ? "Primljeno" : "Poslano / isplata"}</span></div>
                <div className="flex justify-between gap-4"><span className="text-white/50">Iznos</span><span className="text-[#FFD60A] font-heading font-bold">{formatMoney(detail.amount)} {detail.currency}</span></div>
                <div className="flex justify-between gap-4"><span className="text-white/50">{detail.type === "CREDIT" ? "Od koga" : "Kome"}</span><span className="text-right">{detail.counterparty_name}{detail.counterparty_username ? ` (@${detail.counterparty_username})` : ""}</span></div>
                <div className="flex justify-between gap-4"><span className="text-white/50">Datum</span><span>{new Date(detail.created_at).toLocaleDateString("hr-HR", { day: "2-digit", month: "2-digit", year: "numeric" })}</span></div>
                <div className="flex justify-between gap-4"><span className="text-white/50">Vrijeme</span><span>{new Date(detail.created_at).toLocaleTimeString("hr-HR")}</span></div>
                <div className="flex justify-between gap-4"><span className="text-white/50">Status</span><StatusPill s={detail.status} /></div>
                <div><div className="text-white/50 mb-1">Opis</div><div className="text-white/80 break-words">{detail.description}</div></div>
                {detail.payment_link_id && <div><div className="text-white/50 mb-1">Uplatni link</div><div className="text-white/60 font-mono text-xs break-all">/pay/{detail.payment_link_id}</div></div>}
                {detail.session_id && <div><div className="text-white/50 mb-1">Stripe sesija</div><div className="text-white/40 font-mono text-xs break-all">{detail.session_id}</div></div>}
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" className="text-white/70" onClick={() => setDetail(null)} data-testid="tx-detail-close">Zatvori</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
          <DialogContent className="bg-[#171717] border-white/10 text-white">
            <DialogHeader><DialogTitle>Ukloniti transakciju?</DialogTitle></DialogHeader>
            <p className="text-white/60 text-sm">Transakcija se briše samo iz vašeg popisa — stanje novčanika ostaje nepromijenjeno.</p>
            <DialogFooter>
              <Button variant="ghost" className="text-white/70" onClick={() => setConfirmDel(null)} data-testid="tx-del-cancel">Odustani</Button>
              <Button className="bg-red-500 hover:bg-red-600 text-white rounded-full" onClick={delTx} data-testid="tx-del-confirm">Obriši</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
